/**
 * i18n — minimal in-house translator.
 *
 * Why no react-i18next: we have ~50 strings and don't need plurals,
 * lazy-loading, or backend integration. A 100-line custom hook keeps
 * the bundle small and the DX simple. If we ever ship 5+ locales or
 * need ICU pluralisation, swap to react-i18next.
 *
 * Usage:
 *
 *     const t = useT();
 *     t("data.title")                           // -> "Data"
 *     t("develop.created_at", { when: "5h" })   // -> "Created 5h"
 *
 * Locale switching:
 *
 *     const { locale, setLocale } = useI18n();
 *     setLocale("sv");
 *
 * Persists to localStorage so the choice survives page reloads.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { en, type Translations } from "./en";
import { sv } from "./sv";

export type Locale = "en" | "sv";

const DICTIONARIES: Record<Locale, Translations> = {
  en,
  sv,
};

const LOCALE_STORAGE_KEY = "meridian.locale";
const DEFAULT_LOCALE: Locale = "en";

// Visible locales — English-only for now. Swedish translation files
// remain in the codebase so we can re-enable bilingual mode later by
// adding `{ code: "sv", label: "Svenska", flag: "🇸🇪" }` here. Keeping
// it locked down to one locale removes the half-translated experience
// where AI emits Swedish but UI labels are English (or vice versa).
export const SUPPORTED_LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇬🇧" },
];

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readInitialLocale(): Locale {
  // Currently we ship English-only. The browser-language sniff and
  // localStorage read are kept disabled until we re-enable other
  // locales — see SUPPORTED_LOCALES.
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readInitialLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      /* ignore — localStorage unavailable in private mode */
    }
  }, []);

  // Update <html lang> so screen readers + browser autotranslation pick up
  // the right language. Also helps developer tooling.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const dict = DICTIONARIES[locale];
      const fallback = DICTIONARIES.en;
      const value = lookup(dict, key) ?? lookup(fallback, key) ?? key;
      return interpolate(value, vars);
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

/** Shorter convenience hook — most callers only need `t`. */
export function useT(): I18nContextValue["t"] {
  return useI18n().t;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
function lookup(
  dict: Translations | object,
  path: string,
): string | undefined {
  let node: unknown = dict;
  for (const part of path.split(".")) {
    if (node && typeof node === "object" && part in (node as object)) {
      node = (node as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(
  template: string,
  vars: Record<string, string | number> | undefined,
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars[key];
    return v === undefined ? `{${key}}` : String(v);
  });
}
